import {
    type ApiError,
    type ApiGroupListResponse,
    type CreateGroup,
    type Group,
    type GroupSort,
    type GroupWithMembers,
    type KnexPaginateArgs,
    type UpdateGroupWithMembers,
} from '@lightdash/common';
import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
    type UseInfiniteQueryOptions,
    type UseQueryOptions,
} from '@tanstack/react-query';
import { type LightdashApi } from '../api';
import { useLightdashApi } from '../providers/LightdashApi/useLightdashApi';
import useToaster from './toaster/useToaster';
import useQueryError from './useQueryError';

const getOrganizationGroupsQuery = async (
    lightdashApi: LightdashApi,
    includeMembers?: number,
    searchQuery?: string,
    paginateArgs?: KnexPaginateArgs,
    sort?: GroupSort,
) => {
    const urlParams = new URLSearchParams({
        ...(paginateArgs
            ? {
                  page: String(paginateArgs.page),
                  pageSize: String(paginateArgs.pageSize),
              }
            : {}),
        ...(includeMembers ? { includeMembers: String(includeMembers) } : {}),
        ...(searchQuery ? { searchQuery } : {}),
        ...(sort
            ? { sortBy: sort.sortBy, sortDirection: sort.sortDirection }
            : {}),
    }).toString();

    return lightdashApi<ApiGroupListResponse['results']>({
        url: `/org/groups${urlParams ? `?${urlParams}` : ''}`,
        method: 'GET',
        body: undefined,
    });
};

export const useOrganizationGroups = (
    {
        searchInput,
        includeMembers,
    }: {
        searchInput?: string;
        includeMembers?: number;
    },
    queryOptions?: UseQueryOptions<
        ApiGroupListResponse['results']['data'],
        ApiError
    >,
) => {
    const lightdashApi = useLightdashApi();

    const setErrorResponse = useQueryError();
    return useQuery<ApiGroupListResponse['results']['data'], ApiError>({
        queryKey: ['organization_groups', includeMembers, searchInput],
        queryFn: async () => {
            return (
                await getOrganizationGroupsQuery(
                    lightdashApi,
                    includeMembers,
                    searchInput,
                )
            ).data;
        },
        onError: (result) => setErrorResponse(result),
        ...queryOptions,
    });
};

export const useInfiniteOrganizationGroups = (
    {
        searchInput,
        includeMembers,
        pageSize,
        sort,
    }: {
        searchInput?: string;
        includeMembers?: number;
        pageSize: number;
        sort?: GroupSort;
    },
    infinityQueryOpts: UseInfiniteQueryOptions<
        ApiGroupListResponse['results'],
        ApiError
    > = {},
) => {
    const lightdashApi = useLightdashApi();

    const setErrorResponse = useQueryError();
    return useInfiniteQuery<ApiGroupListResponse['results'], ApiError>({
        queryKey: [
            'organization_groups',
            includeMembers,
            pageSize,
            searchInput,
            sort,
        ],
        queryFn: async ({ pageParam }) => {
            return getOrganizationGroupsQuery(
                lightdashApi,
                includeMembers,
                searchInput,
                {
                    pageSize: pageSize,
                    page: pageParam ?? 1,
                },
                sort,
            );
        },
        onError: (result) => setErrorResponse(result),
        getNextPageParam: (lastPage) => {
            if (lastPage.pagination) {
                return lastPage.pagination.page <
                    lastPage.pagination.totalPageCount
                    ? lastPage.pagination.page + 1
                    : undefined;
            }
        },
        ...infinityQueryOpts,
    });
};

const createGroupQuery = async (
    lightdashApi: LightdashApi,
    data: CreateGroup,
) =>
    lightdashApi<GroupWithMembers>({
        url: `/org/groups`,
        method: 'POST',
        body: JSON.stringify(data),
    });

export const useGroupCreateMutation = () => {
    const lightdashApi = useLightdashApi();

    const queryClient = useQueryClient();
    const { showToastSuccess, showToastApiError } = useToaster();
    return useMutation<GroupWithMembers, ApiError, CreateGroup>(
        (data) => createGroupQuery(lightdashApi, data),
        {
            mutationKey: ['create_group'],
            onSuccess: async (_) => {
                await queryClient.invalidateQueries(['organization_groups']);
                await queryClient.invalidateQueries(['organization_users']);

                showToastSuccess({
                    title: `Success! Group was created.`,
                });
            },
            onError: ({ error }) => {
                showToastApiError({
                    title: `Failed to create group`,
                    apiError: error,
                });
            },
        },
    );
};

const updateGroupQuery = async (
    lightdashApi: LightdashApi,
    data: UpdateGroupWithMembers & { uuid: string },
) =>
    lightdashApi<GroupWithMembers>({
        url: `/groups/${data.uuid}`,
        method: 'PATCH',
        body: JSON.stringify({
            name: data.name,
            members: data.members,
        }),
    });

export const useGroupUpdateMutation = () => {
    const lightdashApi = useLightdashApi();

    const queryClient = useQueryClient();
    const { showToastSuccess, showToastApiError } = useToaster();
    return useMutation<
        Group,
        ApiError,
        UpdateGroupWithMembers & { uuid: string }
    >((data) => updateGroupQuery(lightdashApi, data), {
        mutationKey: ['update_group'],
        onSuccess: async (group) => {
            await queryClient.invalidateQueries(['organization_groups']);
            await queryClient.invalidateQueries(['organization_users']);
            showToastSuccess({
                title: `Success! Group '${group.name}' was updated.`,
            });
        },
        onError: ({ error }) => {
            showToastApiError({
                title: `Failed to update group`,
                apiError: error,
            });
        },
    });
};

const deleteGroupQuery = async (lightdashApi: LightdashApi, data: Group) =>
    lightdashApi<Group>({
        url: `/groups/${data.uuid}`,
        method: 'DELETE',
        body: undefined,
    });

export const useGroupDeleteMutation = () => {
    const lightdashApi = useLightdashApi();

    const queryClient = useQueryClient();
    const { showToastSuccess, showToastApiError } = useToaster();
    return useMutation<Group, ApiError, Group>(
        (data) => deleteGroupQuery(lightdashApi, data),
        {
            mutationKey: ['delete_group'],
            onSuccess: async (_, deletedGroup) => {
                await queryClient.invalidateQueries(['organization_groups']);
                await queryClient.invalidateQueries(['organization_users']);

                showToastSuccess({
                    title: `Success! Group '${deletedGroup.name}' was deleted.`,
                });
            },
            onError: ({ error }) => {
                showToastApiError({
                    title: `Failed to delete group`,
                    apiError: error,
                });
            },
        },
    );
};
